import { Pog, PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import type { PlanogramItemCommonNonCircular } from "@CommonTypes/merchflow/pog/item";
import type { GeneratedLayout, StudioItem } from "../domain/studioTypes";

interface ShelfSlot {
  bayIndex: number;
  shelf: { items: unknown[]; uniqueId: string };
  xLeftCm: number;
  xRightCm: number;
}

/** Keep stored total when unresized — width is a ceiling (5/2 rows measures 3, not 6). */
const totalFacingsFor = (
  base: Pick<PlanogramItemCommonNonCircular, "facings" | "facingsRows">,
  hFacings: number,
): number => {
  const rows = Math.max(base.facingsRows || 1, 1);
  const storedWidth = Math.ceil((base.facings || 0) / rows);
  return hFacings === storedWidth ? base.facings : hFacings * rows;
};

export const layoutToPog = (
  basePog: Pog,
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  runIndex: number,
  unrangedSkus: readonly string[] = [],
): Pog => {
  const data = JSON.parse(JSON.stringify(basePog.toNonCircular())) as PogNonCircular;

  const baseBySku = new Map<string, PlanogramItemCommonNonCircular>();
  const homeShelfBySku = new Map<string, string>();
  for (const bay of data.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (const item of shelf.items) {
        const key = item.productCode || item.id;
        if (!baseBySku.has(key)) {
          baseBySku.set(key, item);
          homeShelfBySku.set(key, shelf.uniqueId);
        }
      }
      shelf.items = [];
    }
  }

  // Slot x must match layout cm; live POGs may store cm × 100.
  const rawBayWidth = data.planogram.bays[0]?.width ?? 0;
  const scale = rawBayWidth > 1000 ? 0.01 : 1;
  const slotsByRank = new Map<number, ShelfSlot[]>();
  let xLeftCm = 0;
  data.planogram.bays.forEach((bay, bayIndex) => {
    const sorted = [...bay.shelves].sort((a, b) => b.notchNo - a.notchNo);
    sorted.forEach((shelf, index) => {
      const rank = index + 1;
      const slots = slotsByRank.get(rank) ?? [];
      slots.push({
        bayIndex,
        shelf,
        xLeftCm,
        xRightCm: xLeftCm + bay.width * scale,
      });
      slotsByRank.set(rank, slots);
    });
    xLeftCm += bay.width * scale;
  });

  const itemsBySku = new Map(items.map((item) => [item.skuId, item]));
  const segments = Object.values(layout.placements)
    .flatMap((placement) =>
      placement.segments
        .filter((segment) => segment.run === runIndex)
        .map((segment, index) => ({ ...segment, skuId: placement.skuId, index })),
    )
    .sort((a, b) => a.shelf - b.shelf || a.xCm - b.xCm);

  const skipped: string[] = [];
  const keepAtHome = (skuId: string): boolean => {
    // Unplaceable items stay on the home shelf with base facings.
    const base = baseBySku.get(skuId);
    const homeId = homeShelfBySku.get(skuId);
    if (!base || !homeId) return false;
    for (const slots of slotsByRank.values()) {
      const slot = slots.find((candidate) => candidate.shelf.uniqueId === homeId);
      if (slot) {
        if (
          !slot.shelf.items.some(
            (it) => (it as { uniqueId?: string }).uniqueId === `${skuId}::home`,
          )
        ) {
          slot.shelf.items.push({ ...base, uniqueId: `${skuId}::home`, shelf: homeId });
        }
        return true;
      }
    }
    return false;
  };
  for (const segment of segments) {
    const base = baseBySku.get(segment.skuId);
    const studioItem = itemsBySku.get(segment.skuId);
    const slots = slotsByRank.get(segment.shelf);
    if (!base || !studioItem || !slots || slots.length === 0) {
      skipped.push(segment.skuId);
      keepAtHome(segment.skuId);
      continue;
    }
    const anchorX = segment.xCm + (studioItem.facingWidthCm * segment.hFacings) / 2;
    const slot =
      slots.find((candidate) => anchorX >= candidate.xLeftCm && anchorX < candidate.xRightCm) ??
      slots[slots.length - 1];
    slot.shelf.items.push({
      ...base,
      uniqueId: `${segment.skuId}::${segment.index}`,
      // `facings` is the TOTAL; writing the studio's horizontal count straight in would divide
      // every stacked pack by its row count.
      facings: totalFacingsFor(base, segment.hFacings),
      shelf: slot.shelf.uniqueId,
    });
  }

  if (skipped.length > 0) {
    // eslint-disable-next-line no-console
    console.error(
      `layoutToPog: ${skipped.length} segment(s) could not be drawn where the layout put them; their SKUs were kept at their home shelf instead (spec §4 invariant 4): ${[...new Set(skipped)].slice(0, 5).join(", ")}${skipped.length > 5 ? "…" : ""}`,
    );
  }

  for (const skuId of unrangedSkus) {
    const base = baseBySku.get(skuId);
    if (!base) continue;
    if (data.unrangedItems.some((item) => (item.productCode || item.id) === skuId)) continue;
    const homeShelf = homeShelfBySku.get(skuId) ?? "";
    data.unrangedItems.unshift({
      ...base,
      shelf: homeShelf,
      potentialShelves: homeShelf ? [homeShelf] : [],
      reasons: [],
    });
  }

  return new Pog(data);
};
